import type { DocumentByPathResult } from './view-document';

import { useSuspenseQuery } from '@apollo/client/react';
import {
  Box,
  Button,
  Heading,
  HStack,
  Link,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Suspense, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { DocumentAttachFileModal } from './document-attach-file-modal';
import { DocumentUploadsTable } from './document-uploads-table';

import { GET_UPLOADS_FOR_DOCUMENT } from '#src/graphql/queries/document-uploads';

function DocumentFilesUploadsTable({
  documentId,
  apiHost,
  userEmail,
}: {
  documentId: string;
  apiHost: string;
  userEmail: string;
}) {
  const { data, refetch } = useSuspenseQuery(GET_UPLOADS_FOR_DOCUMENT, {
    variables: { documentId, limit: 200 },
    fetchPolicy: 'cache-first',
  });
  const [uploadOpen, setUploadOpen] = useState(false);

  const rows = data?.uploadsForDocument ?? [];

  return (
    <VStack gap={4} align="stretch" width="100%">
      <HStack gap={2} flexWrap="wrap">
        <Button size="sm" onClick={() => void refetch()}>
          Refresh
        </Button>
        <Button
          size="sm"
          colorScheme="blue"
          onClick={() => setUploadOpen(true)}
        >
          Upload file…
        </Button>
      </HStack>
      <DocumentAttachFileModal
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        apiHost={apiHost}
        userEmail={userEmail}
        documentId={documentId}
        onUploaded={() => void refetch()}
      />
      <DocumentUploadsTable rows={rows} apiHost={apiHost} />
    </VStack>
  );
}

export function DocumentFilesPage({
  documentResult,
  requestedPath,
  apiHost,
  userEmail,
}: {
  documentResult: DocumentByPathResult;
  requestedPath: string;
  apiHost: string;
  userEmail: string;
}) {
  const doc = documentResult?.__typename === 'Document' ? documentResult : null;

  if (!doc) {
    return (
      <Box width="100%" p={4} maxW="2xl" mx="auto">
        <VStack gap={3} align="stretch">
          <Heading size="md" color="fg">
            Files
          </Heading>
          <Text color="fg.muted" fontSize="sm">
            No document at “{requestedPath}”. Create the document first, then
            upload files from the editor.
          </Text>
          <Link
            asChild
            color="blue.600"
            fontSize="sm"
            _hover={{ textDecoration: 'underline' }}
          >
            <RouterLink to={`/documents/${requestedPath}`}>
              Open document path
            </RouterLink>
          </Link>
        </VStack>
      </Box>
    );
  }

  return (
    <Box width="100%" p={4} maxW="4xl" mx="auto">
      <VStack gap={4} align="stretch">
        <VStack gap={1} align="stretch">
          <Heading size="lg" color="fg">
            Files
          </Heading>
          <Text color="fg.muted" fontSize="sm">
            Uploads linked to this document (newest first).
          </Text>
          <Link
            asChild
            color="blue.600"
            fontSize="sm"
            _hover={{ textDecoration: 'underline' }}
          >
            <RouterLink to={`/documents/${doc.path}`}>
              Back to document
            </RouterLink>
          </Link>
        </VStack>
        <Suspense fallback={<Text color="fg.muted">Loading uploads…</Text>}>
          <DocumentFilesUploadsTable
            documentId={doc.id}
            apiHost={apiHost}
            userEmail={userEmail}
          />
        </Suspense>
      </VStack>
    </Box>
  );
}
